import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Engineering as EngineeringIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Cable as CableIcon,
  Memory as MemoryIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  CloudOff as CloudOffIcon,
  Timer as TimerIcon,
  Lock as LockIcon,
  Router as RouterIcon,
  Dns as DnsIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MachineForm from './MachineForm';
import OPCUANodesManager from './OPCUANodesManager';

const MachineManagement = () => {
  const navigate = useNavigate();
  const [machines, setMachines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMachine, setSelectedMachine] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [machineToDelete, setMachineToDelete] = useState(null);
  const [testingConnection, setTestingConnection] = useState(null);
  const [nodesDialogOpen, setNodesDialogOpen] = useState(false);
  const [machineForNodes, setMachineForNodes] = useState(null);
  const [connectionTestResult, setConnectionTestResult] = useState(null);
  const [connectionTestDialogOpen, setConnectionTestDialogOpen] = useState(false);

  // Helper function to get error icon based on error code
  const getErrorIcon = (errorCode) => {
    const iconMap = {
      'TIMEOUT': <TimerIcon color="error" />,
      'GATEWAY_OFFLINE': <CloudOffIcon color="error" />,
      'GATEWAY_TIMEOUT': <CloudOffIcon color="warning" />,
      'GATEWAY_CONNECTION_REFUSED': <CloudOffIcon color="error" />,
      'AUTH_FAILED': <LockIcon color="error" />,
      'CONNECTION_REFUSED': <ErrorIcon color="error" />,
      'HOST_NOT_FOUND': <DnsIcon color="error" />,
      'NETWORK_UNREACHABLE': <RouterIcon color="error" />,
      'NO_ENDPOINT': <WarningIcon color="warning" />,
      'MACHINE_NOT_FOUND': <ErrorIcon color="error" />
    };
    return iconMap[errorCode] || <ErrorIcon color="error" />;
  };

  useEffect(() => {
    loadMachines();
  }, []);

  const loadMachines = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/machines');
      setMachines(response.data);
    } catch (error) {
      console.error('Error loading machines:', error);
      setError('Errore nel caricamento delle macchine');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setSelectedMachine(null);
    setOpenDialog(true);
  };

  const handleEdit = (machine) => {
    setSelectedMachine(machine);
    setOpenDialog(true);
  };

  const handleDelete = (machine) => {
    setMachineToDelete(machine);
    setDeleteConfirmOpen(true);
  };

  const confirmDelete = async () => {
    try {
      await axios.delete(`/api/machines/${machineToDelete.id}`);
      setDeleteConfirmOpen(false);
      setMachineToDelete(null);
      loadMachines();
    } catch (error) {
      console.error('Error deleting machine:', error);
      setError(error.response?.data?.error || 'Errore nell\'eliminazione della macchina');
    }
  };

  const handleTestConnection = async (machine) => {
    try {
      setTestingConnection(machine.id);
      const response = await axios.post(`/api/machines/${machine.id}/test-connection`);

      setConnectionTestResult({
        machine: machine,
        success: response.data.success,
        message: response.data.message,
        serverInfo: response.data.serverInfo,
        details: response.data.details,
        gateway: response.data.gateway,
        testedAt: response.data.testedAt
      });
      setConnectionTestDialogOpen(true);

    } catch (error) {
      console.error('Error testing connection:', error);
      const errorData = error.response?.data || {};

      setConnectionTestResult({
        machine: machine,
        success: false,
        error: errorData.error || error.message || 'Errore sconosciuto',
        errorCode: errorData.errorCode || 'UNKNOWN_ERROR',
        details: errorData.details,
        gateway: errorData.gateway,
        testedAt: errorData.testedAt || new Date().toISOString()
      });
      setConnectionTestDialogOpen(true);
    } finally {
      setTestingConnection(null);
    }
  };

  const handleCloseConnectionTestDialog = () => {
    setConnectionTestDialogOpen(false);
    setConnectionTestResult(null);
  };

  const handleOpenNodes = (machine) => {
    setMachineForNodes(machine);
    setNodesDialogOpen(true);
  };

  const handleCloseNodes = () => {
    setNodesDialogOpen(false);
    setMachineForNodes(null);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setSelectedMachine(null);
  };

  const handleSaveSuccess = () => {
    handleDialogClose();
    loadMachines();
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      'active': { label: 'Attiva', color: 'success', icon: <CheckCircleIcon /> },
      'inactive': { label: 'Inattiva', color: 'default', icon: null },
      'maintenance': { label: 'Manutenzione', color: 'warning', icon: <EngineeringIcon /> },
      'error': { label: 'Errore', color: 'error', icon: <ErrorIcon /> }
    };

    const config = statusConfig[status] || statusConfig['inactive'];
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        icon={config.icon}
      />
    );
  };

  const columns = [
    {
      field: 'status',
      headerName: 'Stato',
      width: 130,
      renderCell: (params) => getStatusChip(params.value)
    },
    {
      field: 'name',
      headerName: 'Nome',
      width: 180,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EngineeringIcon fontSize="small" color="primary" />
          <strong>{params.value}</strong>
        </Box>
      )
    },
    { field: 'code', headerName: 'Codice', width: 120 },
    {
      field: 'location',
      headerName: 'Ubicazione',
      width: 150,
      valueGetter: (params) => params.row.location || 'N/A'
    },
    {
      field: 'opcua_endpoint_url',
      headerName: 'Endpoint OPC UA',
      width: 250,
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <CableIcon fontSize="small" />
            <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {params.value}
            </Typography>
          </Box>
        </Tooltip>
      )
    },
    {
      field: 'max_speed',
      headerName: 'Velocità Max',
      width: 120,
      type: 'number',
      valueFormatter: (params) => params.value ? `${params.value} pz/min` : 'N/A'
    },
    {
      field: 'active_orders',
      headerName: 'Ordini Attivi',
      width: 120,
      type: 'number',
      valueGetter: (params) => params.row.active_orders || 0
    },
    {
      field: 'actions',
      headerName: 'Azioni',
      width: 220,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Dati Live">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => navigate(`/machines/${params.row.id}`)}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Test connessione">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleTestConnection(params.row)}
              disabled={testingConnection === params.row.id}
            >
              {testingConnection === params.row.id ? (
                <CircularProgress size={20} />
              ) : (
                <CableIcon />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Nodi OPC UA">
            <IconButton
              size="small"
              color="secondary"
              onClick={() => handleOpenNodes(params.row)}
            >
              <MemoryIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Modifica">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEdit(params.row)}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Elimina">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(params.row)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )
    }
  ];

  if (loading && machines.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h1">
            Anagrafica Macchine
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadMachines}
            >
              Aggiorna
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAdd}
            >
              Nuova Macchina
            </Button>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={machines}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            disableSelectionOnClick
            loading={loading}
            localeText={{
              noRowsLabel: 'Nessuna macchina censita',
              footerRowSelected: (count) => `${count} riga${count !== 1 ? 'he' : ''} selezionata${count !== 1 ? 'e' : ''}`,
              footerTotalRows: 'Righe totali:',
              columnMenuSortAsc: 'Ordine crescente',
              columnMenuSortDesc: 'Ordine decrescente',
              columnMenuFilter: 'Filtro',
              columnMenuHideColumn: 'Nascondi colonna',
              columnMenuShowColumns: 'Mostra colonne'
            }}
          />
        </Box>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedMachine ? 'Modifica Macchina' : 'Nuova Macchina'}
        </DialogTitle>
        <DialogContent>
          <MachineForm
            machine={selectedMachine}
            onSuccess={handleSaveSuccess}
            onCancel={handleDialogClose}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Conferma Eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare la macchina <strong>{machineToDelete?.name}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Questa operazione non può essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>
            Annulla
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Elimina
          </Button>
        </DialogActions>
      </Dialog>

      {/* OPC UA Nodes Dialog */}
      <Dialog
        open={nodesDialogOpen}
        onClose={handleCloseNodes}
        maxWidth="lg"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          {machineForNodes && (
            <OPCUANodesManager
              machineId={machineForNodes.id}
              machineName={machineForNodes.name}
              onClose={handleCloseNodes}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Connection Test Result Dialog */}
      <Dialog
        open={connectionTestDialogOpen}
        onClose={handleCloseConnectionTestDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          bgcolor: connectionTestResult?.success ? 'success.light' : 'error.light',
          color: connectionTestResult?.success ? 'success.contrastText' : 'error.contrastText'
        }}>
          {connectionTestResult?.success ? (
            <CheckCircleIcon />
          ) : (
            getErrorIcon(connectionTestResult?.errorCode)
          )}
          Test Connessione: {connectionTestResult?.machine?.name}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {connectionTestResult?.success ? (
            // Success case
            <Box>
              <Alert severity="success" sx={{ mb: 2 }}>
                {connectionTestResult.message || 'Connessione riuscita!'}
              </Alert>

              {connectionTestResult.serverInfo && Object.keys(connectionTestResult.serverInfo).length > 0 && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Informazioni Server OPC UA:
                  </Typography>
                  <List dense>
                    {connectionTestResult.serverInfo.productName && (
                      <ListItem>
                        <ListItemIcon><InfoIcon color="primary" /></ListItemIcon>
                        <ListItemText
                          primary="Nome Prodotto"
                          secondary={connectionTestResult.serverInfo.productName}
                        />
                      </ListItem>
                    )}
                    {connectionTestResult.serverInfo.manufacturerName && (
                      <ListItem>
                        <ListItemIcon><EngineeringIcon color="primary" /></ListItemIcon>
                        <ListItemText
                          primary="Produttore"
                          secondary={connectionTestResult.serverInfo.manufacturerName}
                        />
                      </ListItem>
                    )}
                    {connectionTestResult.serverInfo.softwareVersion && (
                      <ListItem>
                        <ListItemIcon><InfoIcon color="primary" /></ListItemIcon>
                        <ListItemText
                          primary="Versione Software"
                          secondary={connectionTestResult.serverInfo.softwareVersion}
                        />
                      </ListItem>
                    )}
                  </List>
                </>
              )}

              {connectionTestResult.gateway && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Testato tramite Gateway:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><RouterIcon color="success" /></ListItemIcon>
                      <ListItemText
                        primary={connectionTestResult.gateway.name}
                        secondary={`IP: ${connectionTestResult.gateway.ip}`}
                      />
                    </ListItem>
                  </List>
                </>
              )}
            </Box>
          ) : (
            // Error case
            <Box>
              <Alert severity="error" sx={{ mb: 2 }}>
                {connectionTestResult?.error || 'Connessione fallita'}
              </Alert>

              {connectionTestResult?.errorCode && (
                <Box sx={{ mb: 2 }}>
                  <Chip
                    label={`Codice: ${connectionTestResult.errorCode}`}
                    size="small"
                    color="error"
                    variant="outlined"
                  />
                </Box>
              )}

              {connectionTestResult?.details && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Dettagli:
                  </Typography>
                  <List dense>
                    {connectionTestResult.details.endpoint && (
                      <ListItem>
                        <ListItemIcon><CableIcon color="action" /></ListItemIcon>
                        <ListItemText
                          primary="Endpoint"
                          secondary={connectionTestResult.details.endpoint}
                        />
                      </ListItem>
                    )}
                    {connectionTestResult.details.hint && (
                      <ListItem>
                        <ListItemIcon><WarningIcon color="warning" /></ListItemIcon>
                        <ListItemText
                          primary="Suggerimento"
                          secondary={connectionTestResult.details.hint}
                        />
                      </ListItem>
                    )}
                    {connectionTestResult.details.originalError && (
                      <ListItem>
                        <ListItemIcon><ErrorIcon color="error" /></ListItemIcon>
                        <ListItemText
                          primary="Errore Originale"
                          secondary={connectionTestResult.details.originalError}
                          secondaryTypographyProps={{ sx: { fontFamily: 'monospace', fontSize: '0.75rem' } }}
                        />
                      </ListItem>
                    )}
                    {connectionTestResult.details.gatewayName && (
                      <ListItem>
                        <ListItemIcon><CloudOffIcon color="error" /></ListItemIcon>
                        <ListItemText
                          primary="Gateway"
                          secondary={`${connectionTestResult.details.gatewayName} - Ultimo heartbeat: ${
                            connectionTestResult.details.lastHeartbeat
                              ? new Date(connectionTestResult.details.lastHeartbeat).toLocaleString('it-IT')
                              : 'mai'
                          }`}
                        />
                      </ListItem>
                    )}
                  </List>
                </>
              )}

              {connectionTestResult?.gateway && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Gateway:
                  </Typography>
                  <List dense>
                    <ListItem>
                      <ListItemIcon><RouterIcon color="action" /></ListItemIcon>
                      <ListItemText
                        primary={connectionTestResult.gateway.name}
                        secondary={connectionTestResult.gateway.ip ? `IP: ${connectionTestResult.gateway.ip}` : 'IP non disponibile'}
                      />
                    </ListItem>
                  </List>
                </>
              )}
            </Box>
          )}

          {connectionTestResult?.testedAt && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Test eseguito: {new Date(connectionTestResult.testedAt).toLocaleString('it-IT')}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConnectionTestDialog} variant="contained">
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MachineManagement;
